import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { humanReadableMs } from '@flakiness/shared/common/utils.js';
import { consume, ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from './api.js';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { DataColumn, DataTableProvider } from './components/data-table.js';
import './components/job-details.js';
import { contexts } from './contexts.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

const jobColumnTypes: Record<string, DataColumn<WireTypes.Job, string>> = {
  status: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span></span>`;
    },

    renderElement(job) {
      if (!job.executionResult)
        return html`<span></span>`;
      if (job.executionResult === 'passed')
        return html`<sl-tooltip content="The job was successful"><sl-icon name=check-circle-fill style="color: green"></sl-icon></sl-tooltip>`;
      if (job.executionResult === 'failed')
        return html`<sl-tooltip content="The job failed"><sl-icon name=x-circle-fill style="color: red"></sl-icon></sl-tooltip>`;
      if (job.executionResult === 'stalled')
        return html`<sl-tooltip content="The job has stalled"><sl-icon name=exclamation-triangle-fill style="color: orange"></sl-icon></sl-tooltip>`;
      return html`<sl-tooltip content="unknown job result - ${job.executionResult}"><sl-icon name=question-circle-fill></sl-icon></sl-tooltip>`;
    },
  },

  id: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>id</span>`;
    },

    renderElement(job) {
      return html`<span>${job.id}</span>`;
    },
  },

  workerName: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>worker name</span>`;
    },

    renderElement(job) {
      return html`<span>${job.workerName ?? '<unset>'}</span>`;
    },
  },

  queueName: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>queue name</span>`;
    },

    renderElement(job) {
      return html`<span>${job.queueName}</span>`;
    },
  },

  duration: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>duration</span>`;
    },

    renderElement(job) {
      return html`${job.executionDurationSeconds ? humanReadableMs(job.executionDurationSeconds * 1000) :
        job.executionTimestampSeconds ? humanReadableMs(Date.now() - job.executionTimestampSeconds * 1000) :
        nothing
      }`;
    },
  },

  submittedAt: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>job submitted</span>`;
    },

    renderElement(job) {
      return html`<sl-relative-time month="long" day="numeric" year="numeric" hour="numeric" minute="numeric" date=${new Date(job.submittedTimestampSeconds * 1000).toISOString()}></sl-relative-time>`;
    },
  },

  queuedFor: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>queued for</span>`;
    },

    renderElement(job) {
      if (!job.executionTimestampSeconds || !job.submittedTimestampSeconds)
        return html``;
      return html`${humanReadableMs((job.executionTimestampSeconds - job.submittedTimestampSeconds) * 1000)}`;
    },
  },

  retry: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>retry</span>`;
    },

    renderElement(job) {
      if (job.retry === 0)
        return html``;
      return html`Retry #${job.retry}`;
    },
  },

  priority: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>priority</span>`;
    },

    renderElement(job) {
      return html`<span>${job.priority}</span>`;
    },
  },

  concurrencyId: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>concurrency ID</span>`;
    },

    renderElement(job) {
      return job.concurrencyId ? html`
        <div style="max-width: 12ch; overflow: hidden; font-family: var(--sl-font-mono); font-size: var(--sl-font-size-small); text-overflow: ellipsis;">${job.concurrencyId}</div>
      ` : html``;
    },
  },

  jobId: {
    sortAxis: undefined,

    width: '1fr',

    renderHeader() {
      return html`<span>job ID</span>`;
    },

    renderElement(job) {
      return html`
        <div style="overflow: hidden; font-family: var(--sl-font-mono); font-size: var(--sl-font-size-small); text-overflow: ellipsis;">${job.jobId}</div>
      `;
    },
  },

  executeAfter: {
    sortAxis: undefined,

    width: 'auto',

    renderHeader() {
      return html`<span>Delay</span>`;
    },

    renderElement(job) {
      if (!job.executeAfterTimestampSeconds)
        return html``;
      const date = new Date(job.executeAfterTimestampSeconds * 1000).toISOString();
      return html`
        <h-box style="gap: var(--sl-spacing-2x-small);">
          <div>
            Execute <sl-tooltip>
            <sl-format-date slot=content month="long" day="numeric" year="numeric" hour="numeric" minute="numeric" hour-format="12" date=${date}></sl-format-date>
            <sl-relative-time date=${date}></sl-relative-time>
            </sl-tooltip>
          </div>
          ${job.queueName === 'cronjobs' ? html`
            <sl-icon-button name="skip-forward-btn" @click=${async (event: Event) => {
              // Don't toggle the row's expanded panel when running the cron job.
              event.stopPropagation();
              await api.administration.runCronJobImmediately.GET({
                jobId: job.jobId,
              });
            }}></sl-icon-button>
          ` : nothing}
        </h-box>
      `;
    },
  },
}

const jobColumns = [...Object.values(jobColumnTypes)];

function renderJobExpandedRow(job: WireTypes.Job) {
  return html`<job-details .job=${job}></job-details>`;
}

@customElement('page-administration-queues')
export class PageAdministrationQueues extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [
        '/administration/queues',
      ],
      render: () => PageAdministrationQueues.page(),
    }];
  }

  static url(): string {
    return platformUrl('/administration/queues');
  }

  static page() {
    return html`<page-administration-queues></page-administration-queues>`;
  }

  @consume({ context: contexts.serverInfo, subscribe: true }) @state() private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  @state() private _archivedExecutionFilter: 'all' | 'non-passed' | 'passed' | 'failed' | 'stalled' = 'non-passed';

  private _jobs = new Task(this, {
    args: () => [this._archivedExecutionFilter] as const,
    task: async ([archivedExecutionFilter], { signal }) => {
      const archivedExecutionResults: ('passed' | 'failed' | 'stalled')[] | undefined =
          archivedExecutionFilter === 'all' ? undefined :
          archivedExecutionFilter === 'non-passed' ? ['failed', 'stalled'] :
          [archivedExecutionFilter];
      return {
        queuedProvider: {
          columns: jobColumns,
          renderExpandedRow: renderJobExpandedRow,
          loader: async (pageOptions, sortOptions, signal) => {
            const result = await api.administration.jobs.GET({
              pageSize: pageOptions.size,
              activePage: 0,
              archivedPage: 0,
              queuedPage: pageOptions.number,
            });
            return result.queued;
          },
        } satisfies DataTableProvider<WireTypes.Job, string>,
        activeProvider: {
          columns: jobColumns,
          renderExpandedRow: renderJobExpandedRow,
          loader: async (pageOptions, sortOptions, signal) => {
            const result = await api.administration.jobs.GET({
              pageSize: pageOptions.size,
              activePage: pageOptions.number,
              archivedPage: 0,
              queuedPage: 0,
            });
            return result.active;
          },
        } satisfies DataTableProvider<WireTypes.Job, string>,
        archivedProvider: {
          columns: jobColumns,
          renderExpandedRow: renderJobExpandedRow,
          loader: async (pageOptions, sortOptions, signal) => {
            const result = await api.administration.jobs.GET({
              pageSize: pageOptions.size,
              activePage: 0,
              archivedPage: pageOptions.number,
              queuedPage: 0,
              archivedExecutionResults,
            });
            return result.archived;
          },
        } satisfies DataTableProvider<WireTypes.Job, string>,
      }
    },
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'Jobs · Dashboard' }),
  });

  override render() {
    return html`
      <user-header submenu=admin-queues></user-header>
      <app-body wide>
        <h1>Queued jobs</h1>
        <data-table
          .provider=${this._jobs.value?.queuedProvider}
          .pageNumber=${0}
          .pageSize=${20}
          .noDataMessage=${'No Queued Jobs'}
        ></data-table>
        <h1>Active jobs</h1>
        <data-table
          .provider=${this._jobs.value?.activeProvider}
          .pageNumber=${0}
          .pageSize=${20}
          .noDataMessage=${'No Active Jobs'}
        ></data-table>
        <h-box style="align-items: baseline; gap: var(--sl-spacing-medium);">
          <h1>Archived jobs</h1>
          <sl-select
            size="small"
            value=${this._archivedExecutionFilter}
            style="width: 10em;"
            @sl-change=${(e: Event) => {
              this._archivedExecutionFilter = (e.target as HTMLSelectElement).value as typeof this._archivedExecutionFilter;
            }}
          >
            <sl-option value="non-passed">Non-passed</sl-option>
            <sl-option value="all">All</sl-option>
            <sl-option value="passed">Passed</sl-option>
            <sl-option value="failed">Failed</sl-option>
            <sl-option value="stalled">Stalled</sl-option>
          </sl-select>
        </h-box>
        <data-table
          .provider=${this._jobs.value?.archivedProvider}
          .pageNumber=${0}
          .pageSize=${20}
          .noDataMessage=${'No Archived Jobs'}
        ></data-table>
      </app-body>
      <app-footer></app-footer>
    `
  }

  static styles = [pageStyles, linkStyles, css`
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-medium);
    }
  `];
}
